import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Mail, Phone, Home, DollarSign, Plus, Wrench, ClipboardList, PawPrint, CalendarClock, Bell, Save, FileDown, UserCheck } from "lucide-react";
import { GhlActionsPanel } from "@/components/GhlIntegration";
import { useAuth } from "@/contexts/AuthContext";

const CHARGE_KINDS = ["dues", "assessment", "late_fee", "adjustment", "credit"];

export default function ResidentDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeForm, setChargeForm] = useState({ kind: "assessment", amount: "", memo: "" });
  const [profileForm, setProfileForm] = useState(null);
  const [emailStatementOpen, setEmailStatementOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [statementForm, setStatementForm] = useState({ since: "", until: "", note: "", cc_admin: false });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/residents/${id}/360`);
      setData(data);
      setProfileForm({
        name: data.resident?.name || "",
        phone: data.resident?.phone || "",
        unit: data.resident?.unit || "",
      });
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Live: any notification, payment, or ledger charge for this resident refetches.
  useSubscribe(id ? `user:${id}` : null, ({ event }) => {
    if (["notification.new", "payment.completed", "ticket.update"].includes(event)) load();
  });

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/residents/${id}`, profileForm);
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const postCharge = async () => {
    const amt = Number(chargeForm.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    // Property Context Guard — always send the property we're 360-ing on.
    const propertyId = data?.resident?.property_ids?.[0] || null;
    try {
      const { data: r } = await api.post(`/admin/ledger/${id}/charge`, {
        kind: chargeForm.kind,
        amount: amt,
        memo: chargeForm.memo || null,
        property_id: propertyId,
      });
      toast.success(`Balance now $${r.new_balance.toFixed(2)}`);
      setChargeOpen(false);
      setChargeForm({ kind: "assessment", amount: "", memo: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const downloadStatement = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/residents/${id}/statement.pdf`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`http ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `statement-${(data?.resident?.name || "resident").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Statement downloaded");
    } catch (e) { toast.error("Statement failed: " + e.message); }
  };

  const emailStatement = async () => {
    setEmailBusy(true);
    try {
      const payload = {
        since: statementForm.since || null,
        until: statementForm.until || null,
        note: statementForm.note || null,
        cc_admin: !!statementForm.cc_admin,
      };
      const { data: r } = await api.post(`/admin/residents/${id}/statement/email`, payload);
      if (r.delivered) {
        toast.success(`Statement emailed to ${r.to}`);
      } else if (r.queued) {
        toast.warning(`Statement queued for ${r.to} — email key isn't configured yet.`);
      } else {
        toast.error(`Email failed: ${r.error || "unknown"}`);
      }
      setEmailStatementOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setEmailBusy(false);
  };

  const { refresh: refreshAuth } = useAuth();

  const impersonate = async () => {
    // No confirm() — banner is prominent and Exit is one click. Every
    // action is audit-logged so misclicks are inconsequential. The
    // impersonation session lives entirely in httpOnly cookies now,
    // so JS never holds the raw JWT.
    try {
      const { data: r } = await api.post(`/admin/impersonate/${id}`);
      toast.success(`Now viewing as ${r.target.name}`);
      // Full reload to reset all in-memory caches (PropertyContext, ledger, etc.)
      const dest = r.target.role === "vendor" ? "/vendor/tickets" : "/resident/dashboard";
      window.location.href = dest;
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const refundTx = async (session_id, max_amount) => {
    const raw = window.prompt(`Refund amount (max $${max_amount.toFixed(2)}):`, max_amount.toFixed(2));
    if (raw == null) return;
    const amt = Number(raw);
    if (!amt || amt <= 0) return;
    const reason = window.prompt("Reason for refund?", "") || "";
    try {
      await api.post(`/admin/payments/transactions/${session_id}/refund`, { amount: amt, reason });
      toast.success("Refund issued");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const kpi = useMemo(() => data?.kpis || {}, [data]);
  const bal = kpi.balance ?? 0;
  const balTone = bal >= 0
    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
    : "border-rose-500/40 text-rose-300 bg-rose-500/10";

  if (!data) return <div className="p-6 text-zinc-400">Loading resident…</div>;
  const r = data.resident;

  return (
    <div>
      <div className="mb-3">
        <Link to="/admin/residents" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-residents">
          <ChevronLeft className="h-3 w-3" /> Residents
        </Link>
      </div>
      <PageHeader
        eyebrow="Resident 360"
        title={r.name}
        subtitle={`${r.email} · Unit ${r.unit || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={balTone} data-testid="resident-balance">
              Ledger: ${bal.toFixed(2)}
            </Badge>
            {kpi.open_tickets > 0 && <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">{kpi.open_tickets} open ticket{kpi.open_tickets === 1 ? "" : "s"}</Badge>}
            {kpi.pending_arc > 0 && <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10">{kpi.pending_arc} ARC pending</Badge>}
            {kpi.open_violations > 0 && <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">{kpi.open_violations} violation{kpi.open_violations === 1 ? "" : "s"}</Badge>}
            <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-200 bg-orange-500/10 hover:bg-orange-500/20" onClick={impersonate} data-testid="impersonate-btn">
              <UserCheck className="h-3.5 w-3.5 mr-1" /> View as resident
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <StatMini icon={DollarSign} label="Balance" value={`$${bal.toFixed(2)}`} tone={bal >= 0 ? "text-emerald-300" : "text-rose-300"} />
        <StatMini icon={Wrench} label="Open tickets" value={kpi.open_tickets ?? 0} />
        <StatMini icon={ClipboardList} label="ARC pending" value={kpi.pending_arc ?? 0} />
        <StatMini icon={PawPrint} label="Pets" value={kpi.pets_registered ?? 0} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="arc" data-testid="tab-arc">ARC</TabsTrigger>
          <TabsTrigger value="pets" data-testid="tab-pets">Pets</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms">Comms</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-white/10 bg-card p-5">
              <div className="overline mb-3">Profile</div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Name</Label><Input value={profileForm?.name || ""} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} data-testid="profile-name" /></div>
                  <div><Label className="text-xs">Unit</Label><Input value={profileForm?.unit || ""} onChange={(e) => setProfileForm((p) => ({ ...p, unit: e.target.value }))} data-testid="profile-unit" /></div>
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={profileForm?.phone || ""} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 010-…" data-testid="profile-phone" />
                </div>
                <Button size="sm" onClick={saveProfile} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-profile">
                  <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
              <div className="mt-4 text-xs text-zinc-500 flex items-center gap-3">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>
                {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                <span className="flex items-center gap-1"><Home className="h-3 w-3" /> {(r.property_ids || []).join(", ")}</span>
              </div>
            </Card>

            <Card className="border-white/10 bg-card p-5">
              <div className="overline mb-3">Communications</div>
              <div className="text-sm text-zinc-300 flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" />
                SMS consent: <Badge variant="outline" className={data.sms_consent?.consent ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-zinc-400"}>
                  {data.sms_consent?.consent ? "opted-in" : "off"}
                </Badge>
              </div>
              <div className="text-xs text-zinc-500 mt-2">Last change: {data.sms_consent?.last_change_at ? new Date(data.sms_consent.last_change_at).toLocaleString() : "—"}</div>
              <div className="mt-4 text-xs text-zinc-500 flex items-center gap-1"><Bell className="h-3 w-3" /> {(data.notifications || []).length} notifications on file</div>
            </Card>

            <div className="md:col-span-2">
              <GhlActionsPanel uid={r.id} contactId={r.ghl_contact_id} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <div className="overline">Ledger activity</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-white/10" onClick={downloadStatement} data-testid="download-statement">
                <FileDown className="h-3.5 w-3.5 mr-1" /> Statement PDF
              </Button>
              <Dialog open={emailStatementOpen} onOpenChange={setEmailStatementOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-white/10" data-testid="email-statement-btn">
                    <Mail className="h-3.5 w-3.5 mr-1" /> Email statement
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader>
                    <DialogTitle><Mail className="h-4 w-4 inline mr-1" /> Email ledger statement</DialogTitle>
                    <DialogDescription className="text-xs text-zinc-400">
                      Sends the full statement PDF to <span className="font-mono text-zinc-200">{r.email}</span> with an inline summary.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Since (optional)</Label>
                        <Input type="date" value={statementForm.since} onChange={(e) => setStatementForm({ ...statementForm, since: e.target.value })} data-testid="email-statement-since" />
                      </div>
                      <div>
                        <Label className="text-xs">Until (optional)</Label>
                        <Input type="date" value={statementForm.until} onChange={(e) => setStatementForm({ ...statementForm, until: e.target.value })} data-testid="email-statement-until" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Personal note (optional)</Label>
                      <Textarea rows={3} value={statementForm.note} onChange={(e) => setStatementForm({ ...statementForm, note: e.target.value })}
                        placeholder="Hi, please review your February statement. Reach out if any charge looks off." data-testid="email-statement-note" />
                    </div>
                    <label className="text-xs text-zinc-300 flex items-center gap-2 select-none">
                      <input type="checkbox" checked={statementForm.cc_admin} onChange={(e) => setStatementForm({ ...statementForm, cc_admin: e.target.checked })} data-testid="email-statement-cc" />
                      Also send a copy to me for records
                    </label>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={emailStatement} disabled={emailBusy} data-testid="email-statement-send">
                      <Mail className="h-3.5 w-3.5 mr-1" /> {emailBusy ? "Sending…" : "Send statement"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="post-charge-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Post charge / credit
                  </Button>
                </DialogTrigger>
              <DialogContent className="bg-card border-white/10">
                <DialogHeader><DialogTitle>Post to ledger</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Kind</Label>
                    <Select value={chargeForm.kind} onValueChange={(v) => setChargeForm({ ...chargeForm, kind: v })}>
                      <SelectTrigger data-testid="charge-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CHARGE_KINDS.map((k) => <SelectItem key={k} value={k}>{k.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[11px] text-zinc-500 mt-1">Credit = money onto their account. Everything else = debit.</div>
                  </div>
                  <div>
                    <Label>Amount (USD)</Label>
                    <Input data-testid="charge-amount" type="number" step="0.01" min="0" value={chargeForm.amount} onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })} />
                  </div>
                  <div>
                    <Label>Memo</Label>
                    <Textarea data-testid="charge-memo" rows={2} value={chargeForm.memo} onChange={(e) => setChargeForm({ ...chargeForm, memo: e.target.value })} placeholder="Late fee for Feb / Special assessment / etc." />
                  </div>
                  <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={postCharge} data-testid="charge-submit">
                    Post to ledger
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          <Card className="border-white/10 bg-card overflow-hidden">
            <div className="divide-y divide-white/5">
              {(data.ledger.charges || []).map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`charge-${c.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-white/10 capitalize text-[10px]">{c.kind}</Badge>
                      <span className="text-zinc-300">{c.memo || "—"}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {new Date(c.at).toLocaleString()} · by {c.actor_name}
                    </div>
                  </div>
                  <div className={"tabular-nums text-base " + (c.amount >= 0 ? "text-emerald-300" : "text-rose-300")}>
                    {c.amount >= 0 ? "+" : ""}${c.amount.toFixed(2)}
                  </div>
                </div>
              ))}
              {(data.ledger.charges || []).length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No manual charges posted yet.</div>}
            </div>
          </Card>

          <div className="overline mt-4 mb-2">Payment transactions</div>
          <Card className="border-white/10 bg-card overflow-hidden">
            <div className="divide-y divide-white/5">
              {(data.ledger.transactions || []).map((tx) => {
                const refunded = tx.refund_amount || 0;
                const remaining = (tx.amount || 0) - refunded;
                return (
                  <div key={tx.session_id} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`tx-${tx.session_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="border-white/10 capitalize text-[10px]">{tx.payment_status}</Badge>
                        <span className="text-zinc-300 truncate">{tx.payment_type} {tx.memo ? `— ${tx.memo}` : ""}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1">{new Date(tx.created_at).toLocaleString()} · {tx.session_id.slice(0, 12)}…</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="tabular-nums">${tx.amount?.toFixed(2)}</div>
                        {refunded > 0 && <div className="text-[10px] text-rose-300">−${refunded.toFixed(2)} refunded</div>}
                      </div>
                      {tx.payment_status === "paid" && remaining > 0.01 && (
                        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => refundTx(tx.session_id, remaining)} data-testid={`refund-${tx.session_id}`}>Refund</Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {(data.ledger.transactions || []).length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No transactions.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tickets" className="mt-4"><ListCards items={data.tickets} field="title" sub="status" testid="ticket" empty="No maintenance tickets." /></TabsContent>
        <TabsContent value="arc" className="mt-4"><ListCards items={data.arc} field="title" sub="status" testid="arc" empty="No ARC requests." /></TabsContent>
        <TabsContent value="pets" className="mt-4"><ListCards items={data.pets} field="pet_name" sub="status" testid="pet" empty="No pets registered." /></TabsContent>
        <TabsContent value="bookings" className="mt-4"><ListCards items={data.bookings} field="amenity_name" sub="status" testid="booking" empty="No bookings on file." /></TabsContent>
        <TabsContent value="comms" className="mt-4">
          <Card className="border-white/10 bg-card overflow-hidden">
            <div className="divide-y divide-white/5">
              {(data.notifications || []).map((n) => (
                <div key={n.id} className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-white/10 capitalize text-[10px]">{n.type}</Badge>
                    <span className="font-medium">{n.title}</span>
                  </div>
                  <div className="text-zinc-400 text-xs mt-1">{n.body}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString()} · {n.read ? "read" : "unread"}</div>
                </div>
              ))}
              {(data.notifications || []).length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No notifications yet.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, tone = "text-zinc-100" }) {
  return (
    <Card className="border-white/10 bg-card p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={"text-2xl font-display tabular-nums mt-1 " + tone}>{value}</div>
    </Card>
  );
}

function ListCards({ items, field, sub, testid, empty }) {
  if (!items || items.length === 0) return <div className="text-sm text-muted-foreground">{empty}</div>;
  return (
    <Card className="border-white/10 bg-card overflow-hidden">
      <div className="divide-y divide-white/5">
        {items.map((i, idx) => (
          <div key={i.id || idx} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`${testid}-${i.id || idx}`}>
            <div>
              <div className="font-medium">{i[field] || "—"}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{i.created_at ? new Date(i.created_at).toLocaleString() : ""}</div>
            </div>
            <Badge variant="outline" className="border-white/10 capitalize">{i[sub] || "—"}</Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}
